import { Asset } from './Asset';
import { BaseModel } from './BaseModel';
import { CustomerAddress } from './Customer';
import InspectionPoint from './inspectionPoint';
import Machine from './machine';
import Operator from './Operator';
import { Preventive } from './Preventive';
import { Downtimes, DowntimesReasons } from './Production/Downtimes';
import type {
  QualityChecklistStatus,
  WorkOrderQualityChecklist,
} from './QualityChecklist';
import SparePart from './SparePart';
import { User, UserType } from './User';

export interface WorkOrder extends BaseModel {
  id: string;
  code: string;
  description: string;
  creationTime: Date;
  startTime: Date | null;
  endTime: Date;
  dueDate?: Date | null;
  stateWorkOrder: StateWorkOrder;
  workOrderType: WorkOrderType;
  machineId?: string;
  machine?: Machine;
  workOrderInspectionPoint?: WorkOrderInspectionPoint[];
  workOrderOperatorTimes?: WorkOrderOperatorTimes[];
  operator?: Operator[];
  operatorId?: string[];
  specialityIds?: string[];
  workOrderSpareParts?: WorkOrderSparePart[];
  workOrderComments?: WorkOrderComment[];
  asset?: Asset;
  workOrderEvents?: WorkOrderEvents[];
  preventive?: Preventive;
  plannedDuration: string;
  operatorsNames?: string;
  originWorkOrder?: OriginWorkOrder;
  downtimeReason?: DowntimesReasons;
  downtimes?: Downtimes[];
  originalWorkOrderId?: string;
  workOrderCreatedId?: string;
  visibleReport?: boolean;
  isPlanned?: boolean;
  customerWorkOrder?: CustomerWorkOrder;
  customerSign?: string;
  workerSign?: string;
  refCustomerId?: string;
  isInvoiced?: boolean;
  qualityChecklists?: WorkOrderQualityChecklist[];
  qualityStatus?: QualityChecklistStatus | null;
  qualityValidatedAt?: string | null;
  qualityValidatedByName?: string | null;
  hasDeliveryNote?: boolean;
  priority: WorkOrderPriority;
  originalWorkOrderCode?: string;
  customer?: string;
  customerName?: string;
  customerInstallationId?: string | null;
  customerInstallationCode?: string;
  customerInstallationCity?: string;
  assetBrand?: string;
  assetDescription?: string;
  derivedCorrectiveId?: string;
  sourceBudgetId?: string;
  sourceBudgetCode?: string;
  sourceBudgetTitle?: string;
  sourceBudgetStateKey?: string | null;
  sourceBudgetStateLabel?: string | null;
  sourceBudgetStateColor?: string | null;
}

export default WorkOrder;

export interface WorkOrderSparePart {
  creationDate?: Date;
  id: string;
  quantity: number;
  sparePart: SparePart;
  operator?: Operator;
  warehouseId: string;
  warehouse: string;
  warehouseName: string;
  serialNumber?: string;
}

export enum StateWorkOrder {
  Waiting,
  OnGoing,
  Paused,
  Finished,
  Requested,
  PendingToValidate,
  Open,
  Closed,
  NotFinished,
  Invoiced,
  InProcess,
}

export enum OriginWorkOrder {
  Maintenance,
  Production,
  Quality,
}

export enum WorkOrderPriority {
  Low = 0,
  Medium = 1,
  High = 2,
  Critical = 3,
}

export interface WorkOrderInspectionPoint {
  id: string;
  check?: boolean;
  inspectionPoint: InspectionPoint;
}

export interface WorkOrderOperatorTimes {
  id?: string;
  startTime: Date;
  endTime?: Date;
  totalTime?: string;
  operator: Operator;
  type?: WorkOrderOperatorTimeType;
  costCategoryId?: string;
}

export enum WorkOrderOperatorTimeType {
  Time,
  Travel,
}

export interface UpdateWorkOrderRequest extends CreateWorkOrderRequest {
  downtimeReason?: DowntimesReasons;
  startTime?: Date;
  visibleReport?: boolean;
  refCustomerId?: string;
  creationTime?: Date;
  active: boolean;
  userId: string;
  operatorLoggedId: string;
  stateChangeReason?: string;
}

export interface UpdateWorkOrderCustomerRequest {
  workOrderId: string;
  customerId: string;
  customerInstallationId: string;
}

export interface CreateWorkOrderRequest {
  id?: string;
  code?: string;
  description: string;
  initialDateTime?: Date;
  stateWorkOrder: StateWorkOrder;
  workOrderType?: WorkOrderType;
  machineId?: string;
  assetId?: string;
  operatorId?: string[];
  inspectionPointId?: string[];
  sparePartId?: string[];
  userId?: string;
  operatorCreatorId: string;
  originWorkOrder: UserType;
  downtimeReasonId?: string;
  originalWorkOrderId?: string;
  originalWorkOrderCode?: string;
  workOrderCreatedId?: string;
  customerId?: string;
  installationId?: string;
  refCustomerId?: string;
  sourceBudgetId?: string;
  dueDate?: Date | null;
  finalDateTime?: Date | null;
}

export interface AddWorkOrderOperatorTimes {
  WorkOrderId: string;
  startTime: Date;
  endTime?: Date;
  operatorId: string;
  workOrderOperatorTimesId?: string;
  costCategoryId?: string;
  type?: WorkOrderOperatorTimeType;
}

export interface UpdateWorkOrderDescriptionRequest {
  description: string;
}

export interface UpdateWorkOrderOperatorsRequest {
  operatorIds: string[];
}

export interface FinishWorkOrderOperatorTimes {
  WorkOrderId: string;
  finishTime: Date;
  operatorId: string;
}

export interface UpdateWorkOrderOperatorTimes {
  workOrderId: string;
  startTime: Date;
  endTime?: Date;
  workOrderOperatorTimesId: string;
  type: WorkOrderOperatorTimeType;
  costCategoryId?: string;
}

export interface DeleteWorkOrderOperatorTimes {
  workOrderId: string;
  workOrderOperatorTimesId: string;
}

export interface SearchWorkOrderFilters {
  machineId?: string;
  startDateTime?: Date;
  endDateTime?: Date;
  operatorId?: string;
  assetId?: string;
  stateWorkOrder?: StateWorkOrder[];
  userType: UserType;
  originWorkOrder: OriginWorkOrder;
  showNextWO?: boolean;
  hasDeliveryNote?: boolean;
  customerId?: string;
  installationId?: string;
  excludeWorkOrderId?: string;
  pendingQuality?: boolean;
  qualityValidated?: boolean;
}

export interface WorkOrderListDto {
  id: string;
  code: string;
  description: string;
  workOrderType: WorkOrderType;
  creationTime: Date;
  startTime: Date | null;
  dueDate?: Date | null;
  stateWorkOrder: StateWorkOrder;
  refCustomerId: string;
  customerName?: string;
  customerInstallationCode?: string;
  customerInstallationCity?: string;
  assetBrand?: string;
  assetDescription?: string;
  originalWorkOrderId?: string;
  originalWorkOrderCode?: string;
  derivedCorrectiveId?: string;
  derivedCorrectiveCode?: string;
  operatorsNames?: string;
  active: boolean;
  hasDeliveryNote: boolean;
  isInvoiced: boolean;
  specialityIds: string[];
  qualityStatus?: QualityChecklistStatus | null;
  qualityValidatedAt?: string | null;
}

export enum WorkOrderType {
  Corrective = 0,
  Preventive = 1,
  Predicitve = 2,
  Ticket = 3,
  Assembly = 4,
}

export interface SaveInspectionResultPointRequest {
  WorkOrderId: string;
  WorkOrderInspectionPointId: string;
  resultInspectionPoint: ResultInspectionPoint;
}

export enum ResultInspectionPoint {
  Pending,
  Ok,
  NOk,
}

export interface WorkOrderComment {
  id?: string;
  creationDate: string;
  comment: string;
  operator: Operator;
  type: WorkOrderCommentType;
  urls: string[];
}

export enum WorkOrderCommentType {
  Internal,
  External,
  NoFinished,
}

export interface AddCommentToWorkOrderRequest {
  comment: string;
  operatorId: string;
  workOrderId: string;
  type: WorkOrderCommentType;
  files?: File[];
  urls?: string[];
}

export interface UpdateWorkOrderCommentRequest {
  comment: string;
  type: WorkOrderCommentType;
  creationDate: string;
  operatorId: string;
}
export interface UpdateStateWorkOrder {
  workOrderId: string;
  state: StateWorkOrder;
  operatorId?: string;
  userId?: string;
  reason?: string;
}

export type WorkOrderInvoicedErrorCode =
  | 'WORKORDER_INVOICED_LOCKED'
  | 'WORKORDER_INVOICED_REASON_REQUIRED'
  | 'WORKORDER_INVOICED_REASON_TOO_LONG';

export interface WorkOrderInvoicedErrorBody {
  code: WorkOrderInvoicedErrorCode;
  workOrderIds: string[];
}

export interface WorkOrderStateChange {
  workOrderId: string;
  state: StateWorkOrder;
}

export interface WorkOrderEvents {
  id: string;
  date: string;
  endDate?: string;
  previousDate?: string;
  newDate?: string;
  workOrderEventType: WorkOrderEventType;
  operator: Operator;
  user?: User;
  reason?: string;
}

export enum WorkOrderEventType {
  Requested = 0,
  Waiting = 1,
  Started = 2,
  Paused = 3,
  PendingToValidate = 4,
  Finished = 5,
  Created = 6,
  NotFinished = 7,
  Open = 8,
  InProcess = 9,
  DateChanged = 10,
  Claimed = 11,
  Invoiced = 12,
  InvoiceRemoved = 13,
  QualityApproved = 16,
  QualityRejected = 17,
}

export interface SaveWorkOrderEventRequest {
  date: string;
  endDate: string | null;
  workOrderEventType: WorkOrderEventType;
  operatorId: string;
}

export interface WorkOrdersFilters {
  workOrderType: WorkOrderType[];
  workOrderState: StateWorkOrder[];
  dateRange: {
    startDate: Date | null;
    endDate: Date | null;
  };
  searchTerm: string;
  assetId: string;
  refCustomerId: string;
  customerName: string;
  isInvoiced: boolean;
  hasDeliveryNote: boolean;
  active: boolean;
  useOperatorLogged: boolean;
  hasCorrectiveCreated: boolean | null;
  onlyUnassigned: boolean;
  pendingQuality: boolean;
  qualityValidated?: boolean;
}

export interface CustomerWorkOrder {
  customerId: string;
  customerName: string;
  customerNif: string;
  customerEmail: string;
  customerPhone: string;
  customerAddress: CustomerAddress;
  customerInstallationId: string;
  customerInstallationCode: string;
  customerInstallationAddress: CustomerAddress;
}
